import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import MovieIcon from "@mui/icons-material/Movie";
import SimCardDownloadIcon from "@mui/icons-material/SimCardDownload";
import SubtitlesIcon from "@mui/icons-material/Subtitles";
import { Avatar, Box, Grid, Typography } from "@mui/material";
import React, { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import LinkBar from "../../components/BreadCrumb/LinkBar";
import HtmlRenderComponent from "../../components/HtmlRenderer/HtmlRenderComponent";
import ReactHtmlParserComponent from "../../components/HtmlRenderer/ReactHtmlParserComponent";
import DetailedContainerLayout from "../../components/Layout/DetailedContainerLayout";
import MainContainer from "../../components/Layout/MainContainer";
import { LoaderComponent } from "../../components/Loader/LoaderComponent";
import MetaDecorator from "../../components/ReusableComponents/MetaDecorator";
import SocialShareComponent from "../../components/SocialShare/SocialShareComponent";
import {
  ButtonPrimary,
  IconWrapperBox,
  MyBox,
  TagsResearchStyled,
  TextUpperCase,
} from "../../components/StyledComponents/StyledComponents";
import useCustomLogic from "../../Hooks/UseCustomLogic";
import useCustomQuery from "../../Hooks/useCustomQuery";
import { peekSsrApiResultSanitized } from "../../services/apiServices/AxiosUtils";
import { URLS } from "../../services/apiServices/service";
import {
  downloadReportUtil,
  handleDateFormat,
  logActivity,
  resolvePublicationImage,
} from "../../utils/utils";
import DialogMedia from "./DialogMedia";
import DilaogArticles from "./DilaogArticles";
import { getResearchCanonicalUrl } from "../../utils/researchRoutes";

// Lazy (not a static top-level import): this component's own import chain
// pulls in react-apexcharts, which touches `window` unconditionally at
// module-require time (not just at render time) - so a static import here
// would crash every SSR render of this screen, even for the vast majority
// of research reports (all except GENDER_DIVERSITY_REPORT_ID) that never
// render this component at all. React.lazy()'s dynamic import() is only
// invoked when React actually attempts to render the lazy component - so
// for every id other than GENDER_DIVERSITY_REPORT_ID, this module (and
// react-apexcharts with it) is never required server-side. Per the earlier
// scoping decision for chart-heavy content: SSR renders the real
// surrounding page, and this specific chart section (only reachable for
// one report) stays client-only, appearing after hydration - it still
// renders in view-source-invisible fashion the same way client-only nav
// interactivity does elsewhere, without blocking or crashing the rest of
// this route's SSR output.
const GenderDiversityReportContent = lazy(() =>
  import("../JaldiPortals/WomenInJudiciary/GenderDiversityReportContent")
);

const GENDER_DIVERSITY_REPORT_ID = "371";

const splitHtmlAfterSection = (htmlString, headingText) => {
  if (
    !htmlString ||
    typeof htmlString !== "string" ||
    typeof DOMParser === "undefined" ||
    !headingText
  ) {
    return { beforeHtml: htmlString, afterHtml: "" };
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${htmlString}</div>`, "text/html");
  const wrapper = doc.body.firstElementChild;

  if (!wrapper) {
    return { beforeHtml: htmlString, afterHtml: "" };
  }

  const normalizedHeading = headingText.trim().toLowerCase();
  const possibleHeadings = Array.from(
    wrapper.querySelectorAll("h1,h2,h3,h4,h5,h6,p,strong,b")
  );
  const matchedNode = possibleHeadings.find(
    (element) => element.textContent?.trim().toLowerCase() === normalizedHeading
  );
  const headingElement =
    matchedNode?.closest("h1,h2,h3,h4,h5,h6,p") ?? matchedNode;

  if (!headingElement) {
    return { beforeHtml: htmlString, afterHtml: "" };
  }

  let insertionTarget = headingElement;
  let nextElement = headingElement.nextElementSibling;

  while (nextElement) {
    insertionTarget = nextElement;

    if (["ol", "ul"].includes(nextElement.tagName.toLowerCase())) {
      break;
    }

    nextElement = nextElement.nextElementSibling;
  }

  const marker = doc.createElement("div");
  marker.setAttribute("data-detail-cta-anchor", "true");
  insertionTarget.after(marker);

  const markerHtml = '<div data-detail-cta-anchor="true"></div>';
  const [beforeHtml, ...afterParts] = wrapper.innerHTML.split(markerHtml);

  if (!afterParts.length) {
    return { beforeHtml: htmlString, afterHtml: "" };
  }

  return {
    beforeHtml,
    afterHtml: afterParts.join(markerHtml),
  };
};

const hasContentData = (value) => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  if (value && typeof value === "object") {
    if (Array.isArray(value.results)) {
      return value.results.length > 0;
    }

    if (Array.isArray(value.data)) {
      return value.data.length > 0;
    }

    return Object.keys(value).length > 0;
  }

  return false;
};

const ResearchDetaiComponent = ({ ctaConfig }) => {
  const { navigate, dispatch, loader } = useCustomLogic();
  const { id } = useCustomQuery();
  const location = useLocation();

  const [openArticlesDialog, setOpenArticlesDialog] = useState(false);
  const [openBlogsDialog, setOpenBlogsDialog] = useState(false);
  const [openMediaDialog, setOpenMediaDialog] = useState(false);

  // Lazy initializer: matches GET_ASSESSING_INFO's saga
  // (getResearchAssessingDetailsApi in researchSaga.js), which requests
  // `${URLS.RESEARCHES}${id}/`. Reads whatever the SSR server already
  // fetched for this route (see server/page-ssr.js) so the first render -
  // the server's renderToString pass, and the client's first
  // pre-hydration pass during hydrateRoot - shows the real report content
  // instead of an empty object. The mount effect below still resets this
  // and re-fetches on every `id` change exactly as before (including the
  // very first mount, where it now just hits this same SSR cache once via
  // consumeSsrApiResult and re-sets state to the same values - a harmless
  // no-op re-render).
  const [researchInfo, setResearchInfo] = useState(
    () => peekSsrApiResultSanitized(`${URLS.RESEARCHES}${id}/`)?.data || {}
  );

  useEffect(() => {
    let isCurrentRequest = true;
    setResearchInfo({});

    dispatch({
      type: "GET_ASSESSING_INFO",
      id,
      successCallBack: (res) => {
        if (!isCurrentRequest) {
          return;
        }

        const nextResearchInfo = res?.data?.data ?? {};
        setResearchInfo(nextResearchInfo);

        const title = nextResearchInfo?.title || "Research Detail";
        logActivity(location.pathname, title);
        dispatch({ type: "HIDE_LOADER" });
      },
    });

    return () => {
      isCurrentRequest = false;
    };
  }, [dispatch, id, location.pathname]);

  const handleOpenArticlesDialog = () => {
    setOpenArticlesDialog(true);
  };

  const handleOpenBlogsDialog = () => {
    setOpenBlogsDialog(true);
  };

  const handleOpenMediaDialog = () => {
    setOpenMediaDialog(true);
  };

  const handleArticlesDialogClose = () => {
    setOpenArticlesDialog(false);
  };

  const handleBlogsDialogClose = () => {
    setOpenBlogsDialog(false);
  };

  const handleMediaDialogClose = () => {
    setOpenMediaDialog(false);
  };

  const reportAttachments = id === GENDER_DIVERSITY_REPORT_ID ? [] : [
    researchInfo?.attachment_attribute,
    researchInfo?.attachment_two_attribute,
    ...(Array.isArray(researchInfo?.report_documents) ? researchInfo.report_documents : []),
  ].filter((attachment) => Boolean(attachment?.link));
  const hasReport = reportAttachments.length > 0;
  const hasArticles = hasContentData(researchInfo?.articles);
  const hasBlogs = hasContentData(researchInfo?.blogs);
  const hasMediaMentions = hasContentData(researchInfo?.media_mentions);
  const hasActionButtons = hasReport || hasArticles || hasBlogs || hasMediaMentions;

  const handleDownloadReport = (attachment) => {
    downloadReportUtil(attachment.link, dispatch, attachment.name ?? null);
  };

  const showDetailCta = ctaConfig?.matchId === id;
  const showGenderDiversityGraphs = id === GENDER_DIVERSITY_REPORT_ID;
  const shouldPlaceDetailCtaInline =
    showDetailCta && ctaConfig?.placement === "afterPolicyRecommendations";

  const htmlSections = useMemo(() => {
    const description = researchInfo?.description;

    if (!shouldPlaceDetailCtaInline) {
      return {
        beforeHtml: description,
        afterHtml: "",
      };
    }

    return splitHtmlAfterSection(
      description,
      ctaConfig?.anchorText ?? "Policy Recommendations"
    );
  }, [
    ctaConfig?.anchorText,
    researchInfo?.description,
    shouldPlaceDetailCtaInline,
  ]);

  const detailCtaUI = () => (
    <>
      <ReactHtmlParserComponent htmlString={`<p>${ctaConfig?.text}</p>`} />
      <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
        <ButtonPrimary onClick={() => navigate(ctaConfig?.path)}>
          {ctaConfig?.buttonLabel}
        </ButtonPrimary>
      </Box>
    </>
  );

  const actionButtonsUI = (isHero = false) => {
    if (!hasActionButtons) {
      return null;
    }

    if (isHero) {
      return (
        <Grid container sx={{ gap: "16px" }}>
          {reportAttachments.map((attachment, index) => (
            <IconWrapperBox key={attachment.id || attachment.link} data-testid="report-download-hero" onClick={() => handleDownloadReport(attachment)}>
              <SimCardDownloadIcon />
              <Typography variant="body1">{attachment.title || (reportAttachments.length > 1 ? `Report ${index + 1}` : "Report")}</Typography>
            </IconWrapperBox>
          ))}
          {hasArticles && (
            <IconWrapperBox onClick={handleOpenArticlesDialog}>
              <SubtitlesIcon />
              <Typography variant="body1">Articles</Typography>
            </IconWrapperBox>
          )}
          {hasBlogs && (
            <IconWrapperBox onClick={handleOpenBlogsDialog}>
              <SubtitlesIcon />
              <Typography variant="body1">Blogs</Typography>
            </IconWrapperBox>
          )}
          {hasMediaMentions && (
            <IconWrapperBox onClick={handleOpenMediaDialog}>
              <MovieIcon />
              <Typography variant="body1">Media Mentions</Typography>
            </IconWrapperBox>
          )}
        </Grid>
      );
    }

    return (
      <Grid container spacing={2} sx={{ mt: "30px", mb: "60px" }}>
        {reportAttachments.map((attachment, index) => (
          <Grid item xs={10} sm={6} md={4} key={attachment.id || attachment.link}>
            <ButtonPrimary
              data-testid="report-download-footer"
              fullWidth
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={() => handleDownloadReport(attachment)}
            >
              {" "}
              {attachment.title || (reportAttachments.length > 1 ? `Download Report ${index + 1}` : "Download the Report here")}
            </ButtonPrimary>
          </Grid>
        ))}
        {hasArticles && (
          <Grid item xs={10} sm={6} md={4}>
            <ButtonPrimary
              fullWidth
              startIcon={<SubtitlesIcon />}
              onClick={handleOpenArticlesDialog}
            >
              {" "}
              Articles
            </ButtonPrimary>
          </Grid>
        )}
        {hasBlogs && (
          <Grid item xs={10} sm={6} md={4}>
            <ButtonPrimary
              fullWidth
              startIcon={<SubtitlesIcon />}
              onClick={handleOpenBlogsDialog}
            >
              {" "}
              Blogs
            </ButtonPrimary>
          </Grid>
        )}
        {hasMediaMentions && (
          <Grid item xs={10} sm={6} md={4}>
            <ButtonPrimary fullWidth startIcon={<MovieIcon />} onClick={handleOpenMediaDialog}>
              Media Mention
            </ButtonPrimary>
          </Grid>
        )}
      </Grid>
    );
  };

  const belowHtmlSectionUI = () => (
    <>
      {showDetailCta && !shouldPlaceDetailCtaInline && detailCtaUI()}

      {shouldPlaceDetailCtaInline && detailCtaUI()}

      {shouldPlaceDetailCtaInline && htmlSections.afterHtml && (
        <ReactHtmlParserComponent htmlString={htmlSections.afterHtml} />
      )}

      <SocialShareComponent />

      {actionButtonsUI()}

      {researchInfo?.tags?.length > 0 && (
        <Grid container sx={{ gap: "24px", mb: "30px" }}>
          <Typography variant="subtitle2">FILED UNDER</Typography>
          <Grid container sx={{ gap: "16px" }}>
            {researchInfo?.tags?.map((item) => (
              <TagsResearchStyled key={item.id}>
                <Typography color="jaldi.main" variant="body2">
                  {item.name}
                </Typography>
              </TagsResearchStyled>
            ))}
          </Grid>
        </Grid>
      )}

      <Grid container direction="column" sx={{ gap: "24px" }}>
        {researchInfo?.research_authors_detail?.length > 0 && (
          <Typography variant="subtitle2">ABOUT THE AUTHORS</Typography>
        )}

        {researchInfo?.research_authors_detail?.length > 0 &&
          researchInfo?.research_authors_detail?.map((item, index) => (
            <Grid container direction="column" key={index} sx={{ gap: "10px" }}>
              <Grid container alignItems={"center"}>
                <Avatar sx={{ width: 36, height: 36 }} src={item?.author?.image_attribute?.link} />
                <TextUpperCase sx={{ ml: 2 }} variant="subtitle2" color="jaldi.main">
                  {`${item?.author?.first_name} ${item?.author?.last_name}`}
                </TextUpperCase>
              </Grid>
              <Typography variant="body1">{item?.author?.description ?? "NA"}</Typography>
            </Grid>
          ))}
      </Grid>
    </>
  );

  const researchImage = resolvePublicationImage(researchInfo);

  return (
    <>
      {researchInfo?.title && <MetaDecorator
        title={researchInfo.title}
        description={researchInfo.seo_description}
        imgUrl={researchImage}
        canonicalUrl={getResearchCanonicalUrl(researchInfo, location.pathname, id)}
        type="article"
      />}
      <LinkBar />
      {loader ? (
        <LoaderComponent width={"100%"} height={"50vh"} />
      ) : (
        <>
          <MainContainer isNoHorizontPadding={true}>
            <DetailedContainerLayout imageCard={researchImage}>
              <Grid item container>
                <Grid item>
                  <TextUpperCase
                    sx={{ borderRight: "1px solid white", pr: 1 }}
                    variant="subtile2"
                    color="jaldi.light"
                  >
                    Report
                  </TextUpperCase>
                </Grid>
                <Grid item>
                  <TextUpperCase sx={{ px: 1 }} variant="subtile2" color="common.white">
                    {handleDateFormat(researchInfo?.date)}
                  </TextUpperCase>
                </Grid>
              </Grid>
              <Typography component="h1" variant="h2" color="common.white">
                {researchInfo?.title}
              </Typography>
              <Grid item sx={{ width: 40, height: 4, backgroundColor: "#DCDCDC" }} />
              <Grid item xs container alignItems="center">
                <MyBox sx={{ flexWrap: "wrap", gap: "10px" }}>
                  {researchInfo?.research_authors_detail?.length > 0 &&
                    researchInfo?.research_authors_detail?.map((item) => (
                      <MyBox key={item?.author?.id}>
                        <Avatar
                          sx={{ width: 32, height: 32 }}
                          src={item?.author?.image_attribute?.link}
                        />
                        <Typography
                          sx={{ ml: 1, cursor: "pointer" }}
                          variant="body1"
                          color="jaldi.light"
                          onClick={() => navigate(`/about/author?id=${item?.author?.id}`)}
                        >
                          {`${item?.author?.first_name} ${item?.author?.last_name}`}
                        </Typography>
                      </MyBox>
                    ))}
                </MyBox>
              </Grid>

              {actionButtonsUI(true)}
            </DetailedContainerLayout>
          </MainContainer>

          <HtmlRenderComponent
            htmlString={htmlSections.beforeHtml}
            htmlContent={
              showGenderDiversityGraphs ? (
                // fallback={null}: renderToString does not wait on
                // Suspense/lazy (it synchronously renders the fallback),
                // so SSR output for this one report simply omits this
                // chart section's markup until the client re-renders post-
                // hydration and the lazy import resolves - consistent with
                // treating this as client-only chart content rather than
                // something SSR needs to produce crawlable markup for.
                <Suspense fallback={null}>
                  <GenderDiversityReportContent htmlString={htmlSections.beforeHtml} />
                </Suspense>
              ) : undefined
            }
            belowHtmlSection={belowHtmlSectionUI}
          />
        </>
      )}

      {openArticlesDialog && hasArticles && (
        <DilaogArticles
          open={openArticlesDialog}
          closeArticlesDialog={handleArticlesDialogClose}
          researchId={id}
          data={researchInfo?.articles}
        />
      )}

      {openBlogsDialog && hasBlogs && (
        <DilaogArticles
          isBlogs={true}
          open={openBlogsDialog}
          closeArticlesDialog={handleBlogsDialogClose}
          researchId={id}
          data={researchInfo?.blogs}
        />
      )}

      {openMediaDialog && hasMediaMentions && (
        <DialogMedia
          open={openMediaDialog}
          closeMediaDialog={handleMediaDialogClose}
          mediaList={researchInfo?.media_mentions}
        />
      )}
    </>
  );
};

export default ResearchDetaiComponent;
